import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Wifi, WifiOff, Clock } from 'lucide-react';
import { BACKEND_URL } from '../../services/config';
import SignalCard from './SignalCard';
import PairSelector from './PairSelector';

/**
 * HomeScreen — "Decision-First" Home Page
 * Shows single best trading signal instantly for the user-selected pair.
 * Selected pair is persisted to localStorage.
 */
const HomeScreen = ({ onNavigate }) => {
  const [pair, setPair] = useState(() => {
    return localStorage.getItem('mbe_selected_pair') || 'XAU/USD';
  });
  const [signal, setSignal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [marketStatus, setMarketStatus] = useState('checking');
  const [lastUpdate, setLastUpdate] = useState(null);

  useEffect(() => {
    localStorage.setItem('mbe_selected_pair', pair);
  }, [pair]);

  const fetchBestSignal = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSignal(null);
    setMarketStatus('checking');
    try {
      const token = localStorage.getItem('fira_auth_token');
      let marketClosedDetail = null;
      let lastNoTrade = null;
      for (const tf of ['M5', 'M15']) {
        try {
          const res = await fetch(`${BACKEND_URL}/api/premium/signals/generate?language=en`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ pair, timeframe: tf }),
          });
          const data = await res.json();

          // Handle 400 Market Closed gracefully
          const errMsg = data.detail || data.error || '';
          if (res.status === 400 && /market.*closed/i.test(errMsg)) {
            marketClosedDetail = errMsg;
            continue;
          }

          if (data.signal && data.signal.direction && data.signal.direction !== 'NEUTRAL' && data.signal.direction !== 'NO TRADE' && data.signal.direction !== 'TIMEOUT') {
            setSignal({ ...data.signal, timeframe: tf, pair });
            setMarketStatus('active');
            setLastUpdate(new Date());
            setLoading(false);
            return;
          }

          // Track the last NO TRADE / NEUTRAL reason so we can show it
          if (data.signal) {
            lastNoTrade = {
              reasons: data.signal.no_trade_reasons || [data.signal.analysis].filter(Boolean),
              confirmations: data.signal.signal_confirmations || [],
              current_price: data.signal.current_price,
            };
          }
        } catch (e) {
          // Try next timeframe
        }
      }
      setSignal(null);
      if (marketClosedDetail) {
        setMarketStatus('closed');
        setError(marketClosedDetail);
      } else if (lastNoTrade) {
        setMarketStatus('no-setup');
        setError(lastNoTrade);
      } else {
        // All attempts failed (LLM timeout / network) — show retry state
        setMarketStatus('retry');
      }
      setLastUpdate(new Date());
    } catch (err) {
      setError(err.message);
      setMarketStatus('error');
    }
    setLoading(false);
  }, [pair]);

  useEffect(() => {
    fetchBestSignal();
  }, [fetchBestSignal]);

  const handleExecute = () => {
    if (signal) onNavigate && onNavigate('trade');
  };
  const handleViewFullSetup = () => {
    if (signal) onNavigate && onNavigate('trade');
  };

  const getStatusInfo = () => {
    if (marketStatus === 'active') return { color: '#10B981', label: 'Market Active', icon: Wifi };
    if (marketStatus === 'scanning') return { color: '#F5A623', label: 'Scanning...', icon: Clock };
    if (marketStatus === 'closed') return { color: '#64748B', label: 'Market Closed', icon: Clock };
    if (marketStatus === 'no-setup') return { color: '#F5A623', label: 'No Setup', icon: Clock };
    if (marketStatus === 'retry') return { color: '#EF4444', label: 'Try again', icon: WifiOff };
    if (marketStatus === 'error') return { color: '#EF4444', label: 'Offline', icon: WifiOff };
    return { color: '#94A3B8', label: 'Checking...', icon: Clock };
  };

  const statusInfo = getStatusInfo();
  const StatusIcon = statusInfo.icon;

  return (
    <div data-testid="home-screen" style={{ fontFamily: 'Inter, -apple-system, sans-serif' }}>
      {/* Market Status Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: '16px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{
            width: '8px', height: '8px', borderRadius: '50%',
            background: statusInfo.color,
            boxShadow: `0 0 8px ${statusInfo.color}40`,
          }} />
          <span style={{ fontSize: '13px', fontWeight: '600', color: '#64748B' }}>
            {statusInfo.label}
          </span>
        </div>
        <button
          data-testid="home-refresh-btn"
          onClick={fetchBestSignal}
          disabled={loading}
          style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            padding: '8px 14px',
            borderRadius: '12px', border: '1px solid #E2E8F0',
            background: '#FFFFFF', color: '#64748B',
            fontSize: '12px', fontWeight: '600',
            cursor: loading ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease',
          }}
        >
          <RefreshCw size={14} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
          {loading ? 'Scanning' : 'Refresh'}
        </button>
      </div>

      {/* Pair selector row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
        <span style={{ fontSize: '12px', fontWeight: '600', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          Pair
        </span>
        <PairSelector value={pair} onChange={setPair} />
      </div>

      {/* Section Label */}
      <div style={{ marginBottom: '16px' }}>
        <h1 style={{
          fontSize: '26px', fontWeight: '700', color: '#0F172A',
          fontFamily: 'Poppins, sans-serif', margin: '0 0 4px 0',
        }}>
          Best Setup
        </h1>
        <p style={{ fontSize: '14px', color: '#94A3B8', margin: 0 }}>
          AI-selected highest confidence opportunity
        </p>
      </div>

      {/* Signal Card */}
      {loading ? (
        <div style={{
          background: '#FFFFFF', borderRadius: '24px',
          padding: '48px 24px',
          border: '1px solid rgba(226, 232, 240, 0.6)',
          boxShadow: '0 8px 30px rgba(0, 0, 0, 0.04)',
          textAlign: 'center',
        }}>
          <div style={{
            width: '48px', height: '48px', borderRadius: '50%',
            border: '3px solid #F1F5F9', borderTopColor: '#F5A623',
            animation: 'spin 0.8s linear infinite',
            margin: '0 auto 16px',
          }} />
          <p style={{ fontSize: '14px', color: '#94A3B8' }}>Analyzing {pair}...</p>
        </div>
      ) : marketStatus === 'closed' ? (
        <div data-testid="market-closed-card" style={{
          background: 'linear-gradient(180deg, #F8FAFC, #FFFFFF)', borderRadius: '24px',
          padding: '40px 24px',
          border: '1px solid rgba(226, 232, 240, 0.8)',
          boxShadow: '0 8px 30px rgba(0, 0, 0, 0.04)',
          textAlign: 'center',
        }}>
          <div style={{
            width: '56px', height: '56px', borderRadius: '50%',
            background: 'rgba(100,116,139,0.08)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px',
          }}>
            <Clock size={26} color="#64748B" />
          </div>
          <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#0F172A', margin: '0 0 6px 0', fontFamily: 'Poppins, sans-serif' }}>
            Market Closed
          </h3>
          <p style={{ fontSize: '13px', color: '#64748B', lineHeight: 1.5, margin: '0 auto', maxWidth: '360px' }}>
            {pair} trades during forex sessions (Monday–Friday). Check back when markets reopen. Crypto pairs (BTC/ETH) trade 24/7.
          </p>
          <div style={{
            marginTop: '14px', display: 'inline-block',
            padding: '6px 12px', borderRadius: '20px', background: '#FFF7ED',
            fontSize: '11px', fontWeight: 600, color: '#B45309',
          }}>
            💡 Try BTC/USD or ETH/USD — crypto never sleeps
          </div>
        </div>
      ) : marketStatus === 'retry' ? (
        <div data-testid="retry-card" style={{
          background: '#FFF', borderRadius: '24px',
          padding: '36px 24px',
          border: '1px solid rgba(239,68,68,0.2)',
          textAlign: 'center',
        }}>
          <div style={{
            width: '52px', height: '52px', borderRadius: '50%',
            background: 'rgba(239,68,68,0.08)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 14px',
          }}>
            <WifiOff size={22} color="#EF4444" />
          </div>
          <h3 style={{ fontSize: '17px', fontWeight: 700, color: '#0F172A', margin: '0 0 6px 0', fontFamily: 'Poppins, sans-serif' }}>
            Signal Engine Busy
          </h3>
          <p style={{ fontSize: '13px', color: '#64748B', lineHeight: 1.5, margin: '0 auto 14px', maxWidth: '340px' }}>
            Our AI is under heavy load right now. Please try refreshing in a few seconds.
          </p>
          <button
            data-testid="retry-btn"
            onClick={fetchBestSignal}
            style={{
              padding: '10px 20px', borderRadius: '10px', border: 'none',
              background: 'linear-gradient(135deg,#F5A623,#E8951A)',
              color: '#FFF', fontSize: '13px', fontWeight: 700, cursor: 'pointer',
            }}
          >
            Try Again
          </button>
        </div>
      ) : marketStatus === 'no-setup' ? (
        <div data-testid="no-setup-card" style={{
          background: 'linear-gradient(180deg, #FFFBF2, #FFFFFF)', borderRadius: '24px',
          padding: '32px 24px',
          border: '1px solid rgba(245,166,35,0.25)',
          boxShadow: '0 8px 30px rgba(245,166,35,0.06)',
          textAlign: 'center',
        }}>
          <div style={{
            width: '56px', height: '56px', borderRadius: '50%',
            background: 'rgba(245,166,35,0.10)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 14px',
          }}>
            <Clock size={26} color="#F5A623" />
          </div>
          <h3 style={{ fontSize: '17px', fontWeight: 700, color: '#0F172A', margin: '0 0 6px 0', fontFamily: 'Poppins, sans-serif' }}>
            No High-Confidence Setup Yet
          </h3>
          <p style={{ fontSize: '13px', color: '#64748B', lineHeight: 1.5, margin: '0 auto 12px', maxWidth: '380px' }}>
            Market is open but our AI hasn't found a strong {pair} opportunity right now. Quality over quantity — we'd rather wait.
          </p>
          {error && typeof error === 'object' && error.current_price && (
            <div style={{
              display: 'inline-block', padding: '6px 14px', borderRadius: '999px',
              background: '#F8FAFC', border: '1px solid #E2E8F0',
              fontSize: '12px', fontWeight: 600, color: '#0F172A',
              marginTop: '4px',
            }}>
              {pair}: <span style={{ color: '#F5A623' }}>${Number(error.current_price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
          )}
          {error && typeof error === 'object' && Array.isArray(error.reasons) && error.reasons.length > 0 && (
            <div style={{
              marginTop: '12px', padding: '10px 12px',
              background: '#FFF', borderRadius: '12px', border: '1px solid #F1F5F9',
              fontSize: '11px', color: '#64748B', textAlign: 'left', lineHeight: 1.5,
            }}>
              <strong style={{ color: '#475569', fontWeight: 700, display: 'block', marginBottom: '4px' }}>Why no trade?</strong>
              {error.reasons.slice(0, 2).map((r, i) => <div key={i}>• {r}</div>)}
            </div>
          )}
        </div>
      ) : (
        <SignalCard
          signal={signal}
          onExecute={handleExecute}
          onViewFullSetup={handleViewFullSetup}
        />
      )}

      {/* Last Update */}
      {lastUpdate && (
        <div style={{
          textAlign: 'center', marginTop: '16px',
          fontSize: '11px', color: '#CBD5E1', fontWeight: '500',
        }}>
          Updated {lastUpdate.toLocaleTimeString('en-MY', { hour: '2-digit', minute: '2-digit' })}
        </div>
      )}
    </div>
  );
};

export default HomeScreen;
